<template>
  <div class="account-card d-flex justify-content-between align-items-center">
    <div class="d-flex align-items-center gap-3">
      <div class="account-icon-wrapper">
        <template v-if="hasImageIcon">
          <img :src="icon" :alt="bank" class="account-icon-image" />
        </template>

        <template v-else-if="hasClassIcon">
          <i :class="icon"></i>
        </template>

        <template v-else>
          <i class="fal fa-wallet"></i>
        </template>
      </div>

      <div class="account-meta">
        <p class="account-name mb-0">{{ bank }}</p>
        <small class="account-type">Conta</small>
      </div>
    </div>

    <div class="text-end">
      <p :class="['account-balance mb-0', isNegative ? 'negative' : 'positive']">
        {{ formattedBalance }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  bank: {
    type: String,
    default: "",
  },
  balance: {
    type: [Number, String],
    default: 0,
  },
  icon: {
    type: String,
    default: "",
  },
  id: {
    type: [Number, String],
    default: null,
  },
  darkmode: {
    type: Boolean,
    default: false,
  },
});

const numericBalance = computed(() => Number(props.balance || 0));

const isNegative = computed(() => numericBalance.value < 0);

const formattedBalance = computed(() => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(numericBalance.value);
});

const hasImageIcon = computed(() => {
  if (!props.icon) return false;

  return (
    props.icon.startsWith("http") ||
    props.icon.startsWith("/") ||
    props.icon.startsWith("data:image") ||
    props.icon.includes(".png") ||
    props.icon.includes(".jpg") ||
    props.icon.includes(".jpeg") ||
    props.icon.includes(".svg") ||
    props.icon.includes(".webp")
  );
});

const hasClassIcon = computed(() => {
  if (!props.icon) return false;
  return !hasImageIcon.value;
});
</script>

<style scoped>
.account-card {
  background: transparent;
  border: 0;
  border-radius: 18px;
  padding: 16px 14px;
  transition: background-color 0.2s ease, transform 0.2s ease;
  min-height: 82px;
}

.account-card:hover {
  background: rgba(18, 57, 95, 0.035);
  transform: translateY(-1px);
}

.account-icon-wrapper {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(18, 57, 95, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #12395f;
  flex-shrink: 0;
}

.account-icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 14px;
}

.account-icon-wrapper i {
  font-size: 18px;
}

.account-meta {
  min-width: 0;
}

.account-name {
  font-size: 15px;
  font-weight: 800;
  color: #31486d;
  line-height: 1.1;
}

.account-type {
  color: #627693;
  font-size: 13px;
}

.account-balance {
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
}

.account-balance.negative {
  color: #c9485b;
}

.account-balance.positive {
  color: #198754;
}
</style>